import { z } from 'zod';
import { CurrencySchema } from './stores.js';
import { MAX_POSITION, MAX_PRICE_MINOR, MAX_QTY } from './money.js';
import { ItemStatusSchema, ItemSourceSchema } from './items.js';
export const TripStatusSchema = z.enum(['planning', 'shopping', 'done']);
/**
 * big   = „Velký nákup": planuje se dopredu ze sablony, vic obchodu za sebou.
 * quick = „Běžný nákup": par veci narychlo, bez planovani, obchod nepovinny.
 */
export const TripKindSchema = z.enum(['big', 'quick']);
/**
 * Cena polozky je z akce v letaku. Pamatuje si, do kdy akce plati a jestli
 * chce kartu (v obchode i na tisku), a stav pred slevou pro „Zrušit akci".
 */
export const TripItemDealSchema = z.object({
    offerId: z.uuid(),
    /** Nazev z letaku — kdyz ho sleva zapsala do poznamky, zruseni ho smaze. */
    name: z.string().max(500),
    validTo: z.iso.datetime().nullable(),
    requiresCard: z.boolean(),
    prev: z.object({
        priceMinor: z.number().int().nonnegative().max(MAX_PRICE_MINOR).nullable(),
        currency: CurrencySchema.nullable(),
        storeId: z.uuid().nullable(),
    }),
});
export const TripItemSchema = z.object({
    id: z.uuid(),
    tripId: z.uuid(),
    catalogItemId: z.uuid().nullable(),
    name: z.string().min(1).max(200),
    /** null = kdekoli (bezny nakup bez obchodu). */
    storeId: z.uuid().nullable(),
    qty: z.number().positive().max(MAX_QTY),
    unit: z.string().max(20).nullable(),
    /** Nullable zamerne: u casti nakupu se cena nehlida vubec. */
    expectedPriceMinor: z.number().int().nonnegative().max(MAX_PRICE_MINOR).nullable(),
    expectedCurrency: CurrencySchema.nullable(),
    status: ItemStatusSchema,
    /** Vyplnuje se, kdyz stav skonci jako found_other_price. */
    actualPriceMinor: z.number().int().nonnegative().max(MAX_PRICE_MINOR).nullable(),
    note: z.string().max(500).nullable(),
    position: z.number().int().nonnegative().max(MAX_POSITION),
    source: ItemSourceSchema,
    /** Kdo polozku pridal — z toho je „doplňují Richard a Sestra". */
    createdBy: z.uuid().nullable(),
    /** Kdo naposledy zmenil stav — avatar u koupene polozky. */
    checkedBy: z.uuid().nullable(),
    updatedAt: z.iso.datetime(),
    updatedBy: z.uuid().nullable(),
    /** null = cena neni z akce. Default kvuli nakupum ulozenym v telefonu starsi verzi. */
    deal: TripItemDealSchema.nullable().default(null),
});
export const TripSchema = z.object({
    id: z.uuid(),
    groupId: z.uuid(),
    kind: TripKindSchema,
    /** Vlastni nazev („Grilovačka u Pepy"); velky nakup bez nazvu se jmenuje datem. */
    title: z.string().max(80).nullable(),
    plannedDate: z.iso.date().nullable(),
    status: TripStatusSchema,
    note: z.string().max(500).nullable(),
    /** Obchody na trase v poradi, jak se projizdi. */
    storeIds: z.array(z.uuid()),
    /** Kurzy se zamrazi pri dokonceni nakupu, aby historicke soucty nekolisaly. */
    fxEurCzk: z.number().positive().nullable(),
    fxPlnCzk: z.number().positive().nullable(),
    createdAt: z.iso.datetime(),
    createdBy: z.uuid().nullable(),
    finishedAt: z.iso.datetime().nullable(),
});
/** Polozky nakupu v jednom obchode; storeId null = bez obchodu. */
export const TripStoreCountSchema = z.object({
    storeId: z.uuid().nullable(),
    items: z.number().int().nonnegative(),
    done: z.number().int().nonnegative(),
});
/** Radek v seznamu nakupu — bez polozek, jen s tim, co je videt na karte. */
export const TripSummarySchema = TripSchema.extend({
    itemCount: z.number().int().nonnegative(),
    doneCount: z.number().int().nonnegative(),
    contributorIds: z.array(z.uuid()),
    /** Nahled nakupu v bocnim panelu. Default kvuli seznamu ulozenemu v telefonu starsi verzi. */
    storeCounts: z.array(TripStoreCountSchema).default([]),
});
export const TripWithItemsSchema = TripSchema.extend({
    items: z.array(TripItemSchema),
});
const tripTitle = z.string().trim().max(80).nullable();
export const TripInputSchema = z.object({
    kind: TripKindSchema,
    title: tripTitle.default(null),
    plannedDate: z.iso.date().nullable().default(null),
    note: z.string().max(500).nullable().default(null),
    storeIds: z.array(z.uuid()).max(20).default([]),
});
export const TripPatchSchema = z.object({
    title: tripTitle.optional(),
    plannedDate: z.iso.date().nullable().optional(),
    status: TripStatusSchema.optional(),
    note: z.string().max(500).nullable().optional(),
    storeIds: z.array(z.uuid()).max(20).optional(),
});
/**
 * Nova polozka. `id` generuje klient, aby se dala pridat i bez signalu —
 * zmena ceka ve fronte a server ji pri opakovanem odeslani nezalozi dvakrat.
 */
export const TripItemInputSchema = z.object({
    id: z.uuid(),
    catalogItemId: z.uuid().nullable().default(null),
    name: z.string().trim().min(1).max(200),
    storeId: z.uuid().nullable().default(null),
    qty: z.number().positive().max(MAX_QTY).default(1),
    unit: z.string().max(20).nullable().default(null),
    expectedPriceMinor: z.number().int().nonnegative().max(MAX_PRICE_MINOR).nullable().default(null),
    expectedCurrency: CurrencySchema.nullable().default(null),
    note: z.string().max(500).nullable().default(null),
    source: ItemSourceSchema.default('manual'),
    deal: TripItemDealSchema.nullable().default(null),
});
export const TripItemPatchSchema = z.object({
    /** Polozka mimo sablonu, kterou nekdo pri planovani do sablony ulozil. */
    catalogItemId: z.uuid().nullable().optional(),
    name: z.string().trim().min(1).max(200).optional(),
    storeId: z.uuid().nullable().optional(),
    qty: z.number().positive().max(MAX_QTY).optional(),
    unit: z.string().max(20).nullable().optional(),
    expectedPriceMinor: z.number().int().nonnegative().max(MAX_PRICE_MINOR).nullable().optional(),
    expectedCurrency: CurrencySchema.nullable().optional(),
    status: ItemStatusSchema.optional(),
    actualPriceMinor: z.number().int().nonnegative().max(MAX_PRICE_MINOR).nullable().optional(),
    note: z.string().max(500).nullable().optional(),
    position: z.number().int().nonnegative().max(MAX_POSITION).optional(),
    deal: TripItemDealSchema.nullable().optional(),
});
/**
 * Zmeny z outboxu klienta. Vsechny upravy polozek nakupu jdou pres frontu,
 * aby planovani i nakupovani fungovalo bez signalu. Sync je zamerne hloupy:
 * novejsi zmena vyhrava po polozkach, protoze dva lide skoro nikdy
 * needituji tutez polozku zaroven. Viz sekce 4.2 navrhu.
 */
const PatchChangeSchema = z.object({
    // Default kvuli zmenam, ktere ve fronte zbyly z verze pred `op`.
    op: z.literal('patch').default('patch'),
    itemId: z.uuid(),
    patch: TripItemPatchSchema,
    /** Cas zmeny na klientu — starsi zmena novejsi stav neprepise. */
    changedAt: z.iso.datetime(),
});
const AddChangeSchema = z.object({
    op: z.literal('add'),
    item: TripItemInputSchema,
    changedAt: z.iso.datetime(),
});
const RemoveChangeSchema = z.object({
    op: z.literal('remove'),
    itemId: z.uuid(),
    changedAt: z.iso.datetime(),
});
// Poradi je dulezite: patch ma `op` s defaultem, takze musi byt posledni.
export const SyncChangeSchema = z.union([AddChangeSchema, RemoveChangeSchema, PatchChangeSchema]);
/** Id polozky, ktere se zmena tyka. */
export function changeItemId(change) {
    return change.op === 'add' ? change.item.id : change.itemId;
}
export const SyncRequestSchema = z.object({
    changes: z.array(SyncChangeSchema).max(200),
});
export const TripsResponseSchema = z.object({ trips: z.array(TripSummarySchema) });
export const SyncResponseSchema = z.object({
    applied: z.array(z.uuid()),
    skipped: z.array(z.uuid()),
    trip: TripSchema,
    items: z.array(TripItemSchema),
});
/** Nazvy, ktere skupina v beznych nakupech kupuje nejcasteji. */
export const FrequentItemsResponseSchema = z.object({ names: z.array(z.string()) });
//# sourceMappingURL=trips.js.map